import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import type { TarifierungKey, VertragsartKey } from "./types";

const useStyles = makeStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 600px)": {
      gridTemplateColumns: "1fr 1fr",
    },
    "@media (min-width: 1024px)": {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
  },
  tile: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.cardBg,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    rowGap: "10px",
    paddingTop: "20px",
    paddingBottom: "18px",
    paddingLeft: "12px",
    paddingRight: "12px",
    minHeight: "168px",
    textAlign: "center",
    ...shorthands.border("2px", "solid", BRAND.border),
    ...shorthands.borderRadius("12px"),
    transitionProperty: "border-color, box-shadow, transform, background-color",
    transitionDuration: "140ms",
    ":hover": {
      ...shorthands.borderColor("rgba(230,106,44,0.6)"),
      transform: "translateY(-2px)",
    },
  },
  tileActive: {
    ...shorthands.borderColor(BRAND.pillarOrange),
    backgroundColor: "rgba(230,106,44,0.06)",
    boxShadow: "0 0 0 4px rgba(230,106,44,0.18)",
  },
  iconWrap: {
    color: BRAND.pillarOrange,
    width: "72px",
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: "1rem",
    fontWeight: 700,
    color: BRAND.ink,
    lineHeight: 1.2,
  },
  desc: {
    fontSize: "0.78rem",
    color: BRAND.muted,
    lineHeight: 1.3,
  },
  buendelNote: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    color: BRAND.muted,
    fontStyle: "italic",
    backgroundColor: "#fff5e6",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    marginTop: "12px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", "#f3d4a3"),
  },
});

const Svg = ({ children }: { children: React.ReactNode }) => (
  <svg
    width="72"
    height="60"
    viewBox="0 0 72 60"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const Icons: Record<TarifierungKey, () => JSX.Element> = {
  standardvertrag: () => (
    <Svg>
      {/* Single contract sheet with house + checkmark */}
      <rect x="14" y="6" width="44" height="48" rx="3" />
      <path d="M22 18h28M22 26h22" />
      <path d="M24 36l8-6 8 6v8H24z" />
      <circle cx="50" cy="44" r="8" fill="currentColor" stroke="none" opacity="0.85" />
      <path d="M46 44l3 3 5-5" stroke="#fff" strokeWidth="2" />
    </Svg>
  ),
  rahmenvereinbarung: () => (
    <Svg>
      {/* Umbrella over a contract */}
      <path d="M36 6c-14 0-24 8-24 18h48c0-10-10-18-24-18z" />
      <path d="M36 24v22" />
      <path d="M36 46c0 4 6 4 6 0" />
      <rect x="22" y="32" width="28" height="22" rx="2" />
      <path d="M28 38h16M28 44h12" />
    </Svg>
  ),
  gebaeudeMitHGHaftpflicht: () => (
    <Svg>
      {/* House + shield (building + liability) */}
      <path d="M10 30l16-14 16 14" />
      <rect x="14" y="30" width="24" height="22" rx="1" />
      <path d="M22 52v-10h8v10" />
      {/* Shield */}
      <path d="M52 12l10 4v10c0 8-5 14-10 16-5-2-10-8-10-16V16z" />
      <path d="M48 24l3 3 5-5" stroke="currentColor" strokeWidth="2" />
    </Svg>
  ),
};

type TarifOption = { key: TarifierungKey; label: string; description: string };

const EINZEL_OPTIONS: TarifOption[] = [
  {
    key: "standardvertrag",
    label: "Standardvertrag",
    description: "Standard-Konditionen Generali.",
  },
  {
    key: "rahmenvereinbarung",
    label: "Rahmenvereinbarung",
    description: "Wird derzeit wie ein Standardvertrag verarbeitet.",
  },
];

const BUENDEL_OPTIONS: TarifOption[] = [
  {
    key: "gebaeudeMitHGHaftpflicht",
    label: "Gebäude + HG-Haftpflicht",
    description:
      "Gebäudeversicherung mit Haus- und Grundbesitzer-Haftpflicht. Wird derzeit wie ein Standardvertrag verarbeitet.",
  },
];

const TarifierungPicker = ({
  vertragsart,
  value,
  onChange,
}: {
  vertragsart: VertragsartKey | "";
  value: TarifierungKey | "";
  onChange: (v: TarifierungKey) => void;
}) => {
  const styles = useStyles();
  const options = vertragsart === "einzel" ? EINZEL_OPTIONS : vertragsart === "buendel" ? BUENDEL_OPTIONS : [];

  if (!options.length) return null;

  return (
    <div>
      <div className={styles.grid}>
        {options.map((t) => {
          const Render = Icons[t.key];
          const active = value === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => onChange(t.key)}
              className={mergeClasses(styles.tile, active && styles.tileActive)}
              aria-pressed={active}
            >
              <span className={styles.iconWrap}>
                <Render />
              </span>
              <span className={styles.title}>{t.label}</span>
              <span className={styles.desc}>{t.description}</span>
            </button>
          );
        })}
      </div>
      {vertragsart === "buendel" && (
        <div className={styles.buendelNote}>
          Der Haus- und Grundbesitzer-Haftpflichtvertrag wird im Anschluss separat angelegt — aktuell verarbeiten wir
          den Bündelvertrag technisch wie einen Standardvertrag.
        </div>
      )}
    </div>
  );
};

export default TarifierungPicker;
